import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { callApiWithOptions, HttpMethod } from "src/services/apiService";
import AdvancedRemediationNav from "./AdvancedRemediationNav";
import {
  formatPhoneInput,
  extractDigits,
  isValidPhoneNumber,
} from "./phoneUtils";
import TrustCallDialerLogs from "./TrustCallDialerLogs";
import WarmupDialerLogs from "./WarmupDialerLogs";
import styles from "./TNRMReputationScan.module.css";


interface TNRMScanResult {
  score: number | null;
  categoryId: number | null;
  isRegistered: boolean;
  noReputation: boolean;
  reputationHistoryJson: string | null;
  callVolumeJson: string | null;
  overrideAuditsJson: string | null;
  crowdSourceEventsJson: string | null;
  rawFindApiJson: string | null;
  diagnosticLog: string | null;
}

const CATEGORY_MAP: Record<number, string> = {
  1: "Spammer",
  2: "Scammer",
  3: "Telemarketer",
  4: "Spoofer",
  5: "Robocaller",
  6: "Non-compliant",
  7: "Public Service",
  8: "Healthcare",
  9: "Debt Collector",
};

const REASON_MAP: Record<string, string> = {
  HP: "Honeypot",
  PE: "Private Exchange",
  AL: "Algorithm",
  NS: "Neighborhood Spoofing",
  CS: "Crowd Source",
  OA: "Override Audit",
  CJ: "Consortium Join",
};

const getScoreColor = (score: number | null): string => {
  if (score === null) return "#697077";
  if (score >= 1) return "#198038";
  if (score === 0) return "#697077";
  return "#da1e28";
};

const parseJsonSafe = (json: string | null | undefined): any[] => {
  if (!json) return [];
  try {
    return JSON.parse(json);
  } catch {
    return [];
  }
};

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatTimestamp = (ts: number): string => {
  if (ts == null || ts === 0) return "N/A";
  // TNRM uses epoch milliseconds; if value looks like seconds (< year 2001 in ms), multiply
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return new Date(ms).toLocaleString();
};

const TNRMReputationScan: React.FC = () => {
  const { phone: phoneParam } = useParams<{ phone?: string }>();
  const navigate = useNavigate();
  const [phoneInput, setPhoneInput] = useState(
    () => (phoneParam ? formatPhoneInput(phoneParam) : "")
  );

  // Keep the URL path in sync with the input so links are shareable.
  useEffect(() => {
    const digits = extractDigits(phoneInput);
    const target =
      digits.length === 10
        ? `/sysadmin/tnrm-scan/${digits}`
        : `/sysadmin/tnrm-scan`;
    if (window.location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [phoneInput, navigate]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<TNRMScanResult | null>(null);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    rawJson: false,
    diagnosticLog: true,
  });

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleScan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidPhoneNumber(phoneInput)) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    setIsLoading(true);
    setError("");
    setResult(null);

    try {
      const digits = extractDigits(phoneInput);
      const response = await callApiWithOptions({
        endpoint: `sysadmin/advanced-remediation/tnrm-scan?phoneNumber=${digits}`,
        method: HttpMethod.GET,
      });
      setResult(response);
    } catch (err: any) {
      setError(err.message || "Failed to scan phone number");
    } finally {
      setIsLoading(false);
    }
  };

  const repHistory = parseJsonSafe(result?.reputationHistoryJson)
    .sort((a: any, b: any) => (b.eventTS || 0) - (a.eventTS || 0));
  const callVolume = parseJsonSafe(result?.callVolumeJson)
    .filter((item: any) => item.callVol > 0)
    .sort((a: any, b: any) => (b.date || 0) - (a.date || 0));
  const overrideAudits = parseJsonSafe(result?.overrideAuditsJson);
  const crowdSource = parseJsonSafe(result?.crowdSourceEventsJson);

  return (
    <div className={styles.container}>
      <AdvancedRemediationNav phoneNumber={extractDigits(phoneInput)} />

      {error && (
        <div className="alert alert-danger" role="alert">
          <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
            {error}
          </pre>
        </div>
      )}

      {/* Search Form */}
      <div className="card mb-3">
        <div className="card-body">
          <form onSubmit={handleScan}>
            <div className="d-flex align-items-end gap-3">
              <div>
                <label className="form-label fw-semibold">Phone Number</label>
                <div className="input-group" style={{ width: "240px" }}>
                  <span className="input-group-text">+1</span>
                  <input
                    type="tel"
                    className="form-control"
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(formatPhoneInput(e.target.value))}
                    placeholder="XXX-XXX-XXXX"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Scanning...
                  </>
                ) : (
                  "Scan TNRM"
                )}
              </button>
              {/*
                External link to TNS Insights — same target as the "Open in TNS (admin)"
                button on the TrustCall details dialog (phone-detail-dialog.js). TNS
                expects the leading 1 country code in the tn query.
              */}
              {isValidPhoneNumber(phoneInput) && (
                <a
                  href={`https://rsp.tnsi.com/insights?tn=1${extractDigits(phoneInput)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary"
                >
                  Open in TNRM ↗
                </a>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Results */}
      {result && (
        <>
          {/* Registration Status */}
          {!result.isRegistered && (
            <div className="alert alert-warning">
              This phone number is not registered in TNRM.
            </div>
          )}
          {result.isRegistered && (
            <div className="alert alert-info">
              This phone number is registered in TNRM.
            </div>
          )}

          {/* TNS Score Cards */}
          <div className="row mb-4">
            <div className="col-md-6">
              <div className={styles.tnsCard}>
                <div className={styles.tnsCardTitle}>TNS Score</div>
                <div
                  className={styles.tnsScoreValue}
                  style={{ color: getScoreColor(result.score) }}
                >
                  {result.score != null ? (result.score > 0 ? `+${result.score}` : result.score) : "N/A"}
                </div>
                <div className={styles.tnsCardFooter}>Range: -4 to +4</div>
              </div>
            </div>
            <div className="col-md-6">
              <div className={styles.tnsCard}>
                <div className={styles.tnsCardTitle}>TNS Score Last Retrieved</div>
                <div className={styles.tnsCardBody}>
                  {new Date().toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Reputation History */}
          <div className={styles.tableSection}>
            <h5 className={styles.tableSectionTitle}>TNS Reputation History</h5>
            {repHistory.length > 0 ? (
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Date/Time ({LOCAL_TZ})</th>
                    <th>Category</th>
                    <th>Score</th>
                    <th>Reason ID</th>
                  </tr>
                </thead>
                <tbody>
                  {repHistory.map((item: any, i: number) => (
                    <tr key={i}>
                      <td>{formatTimestamp(item.eventTS)}</td>
                      <td>
                        {CATEGORY_MAP[item.tagHistory?.category] ||
                          item.tagHistory?.category}
                      </td>
                      <td
                        style={{
                          color: getScoreColor(item.tagHistory?.score),
                          fontWeight: 600,
                        }}
                      >
                        {item.tagHistory?.score != null
                          ? (item.tagHistory.score > 0 ? `+${item.tagHistory.score}` : item.tagHistory.score)
                          : ""}
                      </td>
                      <td>
                        {REASON_MAP[item.reasonId] || item.reasonId}
                        {item.reasonId && REASON_MAP[item.reasonId] && (
                          <span style={{ color: "#697077", marginLeft: "0.4rem" }}>({item.reasonId})</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className={styles.emptyMessage}>No reputation history found.</p>
            )}
          </div>

          {/* Override Audits */}
          <div className={styles.tableSection}>
            <h5 className={styles.tableSectionTitle}>TNS Override Audits</h5>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Date/Time ({LOCAL_TZ})</th>
                  <th>Action</th>
                  <th>Category</th>
                  <th>Score ID</th>
                  <th>Updated By</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {overrideAudits.length > 0 ? (
                  overrideAudits.map((item: any, i: number) => (
                    <tr key={i}>
                      <td>
                        {item.dateTime
                          ? new Date(item.dateTime).toLocaleString()
                          : formatTimestamp(item.eventTS)}
                      </td>
                      <td>{item.action || "N/A"}</td>
                      <td>
                        {CATEGORY_MAP[item.categoryId] || item.category || item.categoryId}
                      </td>
                      <td
                        style={{
                          color: getScoreColor(item.scoreId ?? item.score),
                          fontWeight: 600,
                        }}
                      >
                        {item.scoreId ?? item.score}
                      </td>
                      <td>{item.updatedBy || item.userName || "N/A"}</td>
                      <td
                        title={item.note}
                        style={{
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.note || ""}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className={styles.emptyMessage}>
                      No override audits found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Call Volume */}
          {callVolume.length > 0 && (
            <div className={styles.tableSection}>
              <h5 className={styles.tableSectionTitle}>TNS Call Volume</h5>
              <div className={styles.scrollContainer}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Call Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callVolume.map((item: any, i: number) => (
                      <tr key={i}>
                        <td>{item.date ? new Date(item.date < 1e12 ? item.date * 1000 : item.date).toLocaleDateString() : "N/A"}</td>
                        <td>{item.callVol?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Crowd Source Events */}
          <div className={styles.tableSection}>
            <h5 className={styles.tableSectionTitle}>TNS Crowd Source Events</h5>
            {crowdSource.length > 0 ? (
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Date/Time ({LOCAL_TZ})</th>
                    <th>Category</th>
                    <th>Source</th>
                    <th>Comment</th>
                    <th>Reported By</th>
                  </tr>
                </thead>
                <tbody>
                  {crowdSource.map((item: any, i: number) => (
                    <tr key={i}>
                      <td>
                        {item.dateTime
                          ? new Date(item.dateTime).toLocaleString()
                          : formatTimestamp(item.eventTS)}
                      </td>
                        <td>{item.csCategory || item.category || "N/A"}</td>
                        <td>{item.source || "N/A"}</td>
                        <td
                          title={item.comment}
                          style={{
                            maxWidth: "250px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.comment || ""}
                        </td>
                        <td>{item.reportedBy || "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            ) : (
              <p className={styles.emptyMessage}>No crowd source events found.</p>
            )}
          </div>

          {/* Raw JSON */}
          {/* Dialer Logs */}
          <TrustCallDialerLogs phoneNumber={extractDigits(phoneInput)} />
          <WarmupDialerLogs phoneNumber={extractDigits(phoneInput)} />

          <div className="card mb-3">
            <div
              className={`card-header fw-semibold ${styles.collapsible}`}
              onClick={() => toggleSection("rawJson")}
            >
              Raw TNRM API Response (JSON)
              <span className={styles.chevron}>
                {expandedSections.rawJson ? "\u25B2" : "\u25BC"}
              </span>
            </div>
            {expandedSections.rawJson && (
              <div className="card-body p-0">
                <pre className={styles.jsonBlock}>
                  {result.rawFindApiJson || "No data"}
                </pre>
              </div>
            )}
          </div>

          {/* Diagnostic Log */}
          <div className="card mb-3">
            <div
              className={`card-header fw-semibold ${styles.collapsible}`}
              onClick={() => toggleSection("diagnosticLog")}
            >
              Diagnostic Log
              <span className={styles.chevron}>
                {expandedSections.diagnosticLog ? "\u25B2" : "\u25BC"}
              </span>
            </div>
            {expandedSections.diagnosticLog && (
              <div className="card-body p-0">
                <pre className={styles.logBlock}>
                  {result.diagnosticLog || "No log data"}
                </pre>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TNRMReputationScan;
